import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Receipt, User, Clock, CreditCard, Undo2, Repeat, ExternalLink, Loader2,
} from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { fetchSale } from '../../redux/slices/saleSlice';

const fmtRs = (n) => `Rs. ${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const statusStyle = (s) => {
  switch (s) {
    case 'PAID': return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300';
    case 'PARTIAL': return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300';
    case 'PENDING': return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300';
    case 'REFUNDED': return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
    default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
  }
};

const refundBadge = (s) => {
  if (s === 'FULL') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300">Fully Refunded / Exchanged</span>;
  if (s === 'PARTIAL') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">Partially Refunded</span>;
  return null;
};

const row = (label, value) => (
  <div className="flex justify-between py-1.5 text-sm">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span className="font-medium text-gray-900 dark:text-white">{value}</span>
  </div>
);

export default function SaleDetailModal({ saleId, onClose, onViewExchange }) {
  const dispatch = useDispatch();
  const { sale, loading, error } = useSelector((state) => state.sales);

  useEffect(() => {
    if (saleId) dispatch(fetchSale(saleId));
  }, [dispatch, saleId]);

  const saleDetail = sale?.saleId === saleId || sale?._id?.toString() === saleId ? sale : null;
  const items = saleDetail?.items || [];
  const payments = saleDetail?.payments || [];
  const refunds = saleDetail?.refunds || [];
  const details = saleDetail?.details || {};

  return (
    <Modal
      isOpen={!!saleId}
      onClose={onClose}
      title={`Order – ${saleDetail?.transactionId || saleDetail?.saleId || ''}`}
      description={saleDetail ? `${saleDetail.saleDate || ''} ${saleDetail.saleTime || ''}` : ''}
      size="lg"
    >
      {loading && !saleDetail ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 size={24} className="animate-spin" />
        </div>
      ) : error && !saleDetail ? (
        <div className="py-10 text-center text-sm text-red-500">{error}</div>
      ) : saleDetail ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium border border-gray-200 dark:border-gray-600 ${statusStyle(saleDetail.paymentStatus)}`}>
              {saleDetail.paymentStatus}
            </span>
            {refundBadge(saleDetail.refundStatus)}
            {saleDetail.isExchangeReplacement && (
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                Exchange Replacement
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <User size={15} className="text-gray-400" />
              Operator: <span className="font-medium">{saleDetail.operator || details.operatorName || saleDetail.staffId || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <CreditCard size={15} className="text-gray-400" />
              Payment: <span className="font-medium">{saleDetail.paymentMethod}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <Clock size={15} className="text-gray-400" />
              {saleDetail.saleDate || ''} at {saleDetail.saleTime || ''}
            </div>
            {saleDetail.customerName && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <User size={15} className="text-gray-400" />
                Customer: <span className="font-medium">{saleDetail.customerName}</span>
              </div>
            )}
            {saleDetail.notes && (
              <div className="text-sm text-gray-500 dark:text-gray-400 sm:col-span-2">
                Notes: {saleDetail.notes}
              </div>
            )}
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Items</h4>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/40 text-left text-xs text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-2 font-medium">Product</th>
                    <th className="px-3 py-2 font-medium text-center">Qty</th>
                    <th className="px-3 py-2 font-medium text-right">Unit</th>
                    <th className="px-3 py-2 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.itemId} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="px-3 py-2">
                        <p className="font-medium text-gray-900 dark:text-white">{it.productName}</p>
                        <p className="text-xs text-gray-400">{it.saleUnit || 'PIECE'}</p>
                      </td>
                      <td className="px-3 py-2 text-center text-gray-600 dark:text-gray-300">{it.quantity}</td>
                      <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">{fmtRs(it.unitPrice)}</td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-white">{fmtRs(it.itemTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3">
              {row('Subtotal', fmtRs(saleDetail.subtotal))}
              {Number(saleDetail.discount) > 0 && row(`Discount (${saleDetail.discountType || 'FIXED'})`, `- ${fmtRs(saleDetail.discount)}`)}
              {Number(saleDetail.taxAmount) > 0 && row('Tax', fmtRs(saleDetail.taxAmount))}
              {row('Total', fmtRs(saleDetail.totalAmount))}
              {row(`Paid (${saleDetail.paymentMethod})`, fmtRs(saleDetail.amountPaid))}
              {Number(saleDetail.amountPending) > 0 && row('Pending', fmtRs(saleDetail.amountPending))}
              {Number(saleDetail.changeAmount) > 0 && row('Change Kept (Profit)', fmtRs(saleDetail.changeAmount))}
              {Number(saleDetail.paymentShortfall) > 0 && (
                <div className="flex justify-between py-1.5 text-sm text-red-500 font-medium">
                  <span>Shortfall (Discount Given)</span>
                  <span>- {fmtRs(saleDetail.paymentShortfall)}</span>
                </div>
              )}
              {Number(saleDetail.refundedAmount) > 0 && (
                <div className="flex justify-between py-1.5 text-sm text-red-500 font-medium">
                  <span>Refunded / Exchanged</span>
                  <span>- {fmtRs(saleDetail.refundedAmount)}</span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              {payments.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Payments</h4>
                  <div className="space-y-1.5">
                    {payments.map((p, i) => (
                      <div key={i} className="flex justify-between text-sm bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-2">
                        <span className="text-gray-600 dark:text-gray-300">{p.type || p.method}</span>
                        <span className="font-medium">{fmtRs(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {refunds.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                    Returns / Exchanges ({refunds.length})
                  </h4>
                  <div className="space-y-1.5">
                    {refunds.map((r) => (
                      <div key={r.refundId} className="bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-200">
                            {r.refundType === 'EXCHANGE' ? <Repeat size={13} className="text-blue-500" /> : <Undo2 size={13} className="text-red-500" />}
                            {r.refundType} – {fmtRs(r.totalRefunded)}
                          </span>
                          {r.exchangeSaleId && onViewExchange && (
                            <Button size="sm" variant="ghost" onClick={() => onViewExchange(r.exchangeSaleId)}>
                              <ExternalLink size={13} /> View
                            </Button>
                          )}
                        </div>
                        {r.reason && <p className="text-xs text-gray-400 mt-0.5">Reason: {r.reason}</p>}
                        {r.items?.map((it, i) => (
                          <p key={i} className="text-xs text-gray-500 dark:text-gray-400">
                            {it.productName} × {it.quantityRefunded}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {saleDetail.exchangeOf && (
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
              <Repeat size={13} className="text-blue-500" />
              This is the exchange replacement for the order below.
              {onViewExchange && (
                <Button size="sm" variant="ghost" onClick={() => onViewExchange(saleDetail.exchangeOf)}>
                  View original
                </Button>
              )}
            </p>
          )}
        </div>
      ) : null}
    </Modal>
  );
}